<template>
  <div class="container">
    <div class="entity-list flex-col mar-top-50">
      <div class="mar-bottom-50">
        <PageHeading
          :title="'New Entity'"
          :description="'Fill in the details to create your entity'"
          :tooltip="
            'Entities are any moving or stationary component in your business with a location & time dimension. They are used to define the relationships between data sources.'
          "
        />
      </div>
      <div class="new-entity mar-bottom-50">
        <div class="flex-col bg-secondary br-5 smooth-shadow mar-bottom-50">
          <!-- CARD HEADER AREA -->
          <div class="header flex-row-align-center pad-5">
            <i class="material-icons icon-button" @click="goBack()"
              >arrow_back</i
            >
            <input
              v-model="entityTitle"
              class="ghost-input custom-input"
              placeholder="Untitled Entity"
              disabled
            />
          </div>
          <!-- CONTENT AREA -->
          <div v-if="isDataAvailable" class="entity-contents">
            <div class="schema-area border-bottom">
              <h3 class="pad-top-10 pad-left-10 pad-right-10">
                <b>Entity Details</b>
                <InfoTooltip
                  :text="
                    'Identification details of your entity. Make it easier for others to understand what your entity is.'
                  "
                />
              </h3>
              <div class="flex-col pad-left-10 mar-top-20">
                <label class="pad-bottom-5">Entity Title</label>
                <input
                  type="text"
                  v-model="entityTitle"
                  placeholder="Enter entity title"
                />
              </div>
              <div class="flex-col pad-left-10 mar-top-10">
                <label class="pad-bottom-5">Entity Description</label>
                <input
                  type="text"
                  v-model="entityDescription"
                  placeholder="Describe your entity"
                />
              </div>
              <!-- Base datasource selection -->
              <h3 class="pad-top-20 pad-left-10 pad-right-10">
                <b>Entity Data</b>
                <InfoTooltip
                  :text="
                    'Base data source is a table/data collection where the data for your entity is available.'
                  "
                />
              </h3>
              <div class="flex-row-align-center pad-left-10 mar-top-10">
                <i class="material-icons pad-right-15 pad-top-10">dns</i>
                <div class="flex-col">
                  <label class="fs-small pad-bottom-5">Data Source</label>
                  <DropDown
                    v-model="baseDataSource"
                    :placeholder="'Data Source'"
                    :options="dataSources"
                    class="mw-200"
                  />
                </div>
                <div class="flex-col" v-if="baseDataSource">
                  <label class="fs-small pad-bottom-5">Entity Id</label>
                  <DropDown
                    v-model="entityKey"
                    :placeholder="'Entity Id'"
                    :options="baseColumns"
                    class="mw-200"
                  />
                </div>
                <div class="flex-col" v-if="baseDataSource">
                  <label class="fs-small pad-bottom-5">TimeStamp Column</label>
                  <DropDown
                    v-model="baseTimeColumn"
                    :placeholder="'Choose one'"
                    :options="getColumnsByType('datetime')"
                    class="mw-200"
                  />
                </div>
              </div>
              <div
                class="flex-row-align-center pad-left-10 mar-top-15"
                v-if="baseDataSource"
              >
                <i class="material-icons pad-right-15 pad-top-10"
                  >view_column</i
                >
                <div class="flex-col">
                  <label class="fs-small pad-bottom-5">Select Columns</label>
                  <MultiSelectDropDown
                    :placeholder="'Columns'"
                    :options="baseColumns"
                    v-model="selectedBaseColumns"
                    class="mw-400"
                    :selectAll="true"
                  />
                </div>
              </div>

              <!-- Additional Data Source selection  -->
              <div class="flex-row-align-center mar-top-20">
                <h3 class="pad-10 flex-expand">
                  <b>Additional Data Sources</b>
                  <InfoTooltip
                    :text="
                      'Select additional tables/data collections where more property/details of your entity is present'
                    "
                  />
                </h3>
              </div>
              <div
                class="flex-row-align-center pad-left-10 mar-top-15"
                v-for="(DS, index) in additional_data_sources"
                :key="index"
              >
                <div class="flex-col">
                  <label class="mar-bottom-5">Data Source</label>
                  <DropDown
                    v-model="DS.dataSource"
                    :placeholder="'Data Source'"
                    :options="getAuxDataSources()"
                    class="mw-200"
                  />
                </div>
                <div class="flex-col" v-if="DS.dataSource">
                  <label class="mar-bottom-5">Entity Id Column</label>
                  <DropDown
                    v-model="DS.keyColumn"
                    :placeholder="'Choose id col'"
                    :options="getAuxColumns(DS.id)"
                    class="mw-200"
                  />
                </div>
                <div class="flex-col" v-if="DS.dataSource">
                  <label class="mar-bottom-5">Select Columns</label>
                  <MultiSelectDropDown
                    :placeholder="'Choose COlumns'"
                    :options="getAuxColumns(DS.id, DS.keyColumn)"
                    v-model="DS.columns"
                    class="mw-400"
                    :selectAll="true"
                  />
                </div>
                <i
                  class="material-icons icon-button md-18 delete-icon"
                  @click="removeDS(DS.id)"
                  >delete</i
                >
              </div>
              <p v-show="!baseDataSource" class="mar-left-10">
                Choose an entity datasource first to add additional datasources
              </p>
              <div>
                <button
                  :class="[
                    'button',
                    baseDataSource ? 'info' : 'inverted',
                    'fs-small',
                    'flex-center',
                    'mar-left-10',
                    'mar-top-20',
                  ]"
                  @click="addAdditionalDataSource()"
                  :disabled="!baseDataSource"
                >
                  <i class="material-icons md-18 mar-right-10">add</i>
                  <b>ADD DATA SOURCE</b>
                </button>
              </div>
            </div>
            <div class="flex-row-align-center pad-15">
              <div class="flex-expand"></div>
              <Spinner v-if="isSaveInProgress" class="mar-right-15" />
              <div v-else class="flex-row-align-center">
                <button
                  class="button inverted mar-right-10 flex-center fs-small"
                  @click="goBack()"
                >
                  <i class="material-icons md-18 mar-right-10">clear</i> CANCEL
                </button>
                <button
                  class="button mar-right-10 flex-center fs-small"
                  @click="saveEntity()"
                >
                  <i class="material-icons md-18 mar-right-10">save</i> SAVE
                </button>
              </div>
            </div>
          </div>
          <div v-else class="pad-30">
            <LoadingBar />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { getRandomId } from "@/lib/idGenerator";

export default {
  data: () => ({
    entityTitle: "",
    entityDescription: "",
    isDataAvailable: true,
    additional_data_sources: [],
    baseDataSource: null,
    entityKey: null,
    selectedBaseColumns: [],
    baseGridColumn: {},
    baseTimeColumn: {},
    isSaveInProgress: false,
  }),
  computed: {
    ...mapGetters("Datasources", ["getDataSources"]),
    /**
     * Compute the columns from the selected [baseDataSource]
     */
    baseColumns() {
      if (!this.baseDataSource) return [];
      const selectIds = [];
      if (this.entityKey) selectIds.push(this.entityKey.id);
      if (this.baseGridColumn) selectIds.push(this.baseGridColumn.id);
      if (this.baseTimeColumn) selectIds.push(this.baseTimeColumn.id);

      return this.baseDataSource.columns.filter(column => {
        return selectIds.find(item => item === column.col_name) ? false : true;
      });
    },
    dataSources() {
      return this.getDataSources;
    },
  },
  methods: {
    ...mapActions("Datasources", ["fetchDataSources"]),
    ...mapActions("Entities", ["createNewEntity", "reset"]),
    goBack() {
      this.$router.go(-1);
    },
    getColumnsWithId(dataSource, filterId) {
      if (!dataSource) return [];
      return dataSource.columns.filter(column => {
        return column.id !== filterId;
      });
    },
    getAuxColumns(aux_id, filterCol) {
      const aux_ds = this.additional_data_sources.find(
        item => item.id === aux_id
      );

      const selectedIds = [];
      if (filterCol) selectedIds.push(filterCol);
      if (aux_ds.entityKey) selectedIds.push(aux_ds.keyColumn.id);
      if (aux_ds.columns.length > 0) selectedIds.push(...aux_ds.columns);

      if (!aux_ds.dataSource) return [];
      else {
        return aux_ds.dataSource.columns.filter(column => {
          return selectedIds.find(item => item === column.id) ? false : true;
        });
      }
    },
    getColumnsByType(infoType) {
      if (!this.baseDataSource) return [];
      const selectIds = [];
      if (this.entityKey) selectIds.push(this.entityKey.id);
      return this.baseDataSource.columns
        .filter(column => {
          return selectIds.find(item => item === column.id) ? false : true;
        })
        .filter(column => {
          return column.info_type === infoType;
        });
    },
    addAdditionalDataSource() {
      this.additional_data_sources.push({
        id: getRandomId(),
        dataSource: "",
        keyColumn: "",
        columns: [],
      });
    },
    removeDS(key) {
      this.additional_data_sources = this.additional_data_sources.filter(
        item => {
          return item.id !== key;
        }
      );
    },
    getAuxDataSources() {
      const selectedDS = [];
      // push all selected datasources
      if (this.baseDataSource) selectedDS.push(this.baseDataSource.id);
      if (this.additional_data_sources.length > 0) {
        this.additional_data_sources.forEach(item => {
          selectedDS.push(item.dataSource.id);
        });
      }

      return this.dataSources.filter(item => {
        return selectedDS.find(id => id === item.id) ? false : true;
      });
    },
    validateAuxDs() {
      if (this.additional_data_sources.length === 0) return true;

      this.additional_data_sources = this.additional_data_sources.filter(
        item => {
          const { dataSource, keyColumn, columns } = item;
          return dataSource && keyColumn;
        }
      );

      for (let i = 0; i < this.additional_data_sources.length; i++) {
        const { dataSource, keyColumn, columns } = this.additional_data_sources[
          i
        ];
        if (!dataSource || !keyColumn) return false;
      }

      return true;
    },
    saveEntity() {
      if (
        !this.baseDataSource ||
        !this.entityTitle ||
        !this.entityDescription ||
        !this.entityKey ||
        // !this.baseGridColumn ||
        !this.baseTimeColumn ||
        !this.validateAuxDs()
      )
        return;
      this.isSaveInProgress = true;

      const data = {
        name: this.entityTitle,
        description: this.entityDescription,
        datasource_id: this.baseDataSource.id,
        entity_id_col: this.entityKey.name,
        selected_columns: this.selectedBaseColumns
          .map(column => column.name)
          .filter(item =>
            item !== this.entityKey.name
              ? item !== this.baseTimeColumn.name
                ? true
                : false
              : false
          ),
        // grid_col: this.baseGridColumn.id,
        grid_col: "__grid_id",
        time_col: this.baseTimeColumn.name,
        aux_ds: this.additional_data_sources.map(ds => ({
          datasource_id: ds.dataSource.id,
          entity_id_col: ds.keyColumn.name,
          selected_columns: ds.columns
            .map(column => column.name)
            .filter(item => item !== ds.keyColumn.name),
        })),
      };

      this.createNewEntity(data)
        .then(response => {
          this.isSaveInProgress = false;
          if (response.status === 200) {
            this.reset().then(() => this.$router.push("/entities"));
          }
        })
        .catch(error => {
          this.isSaveInProgress = false;
        });
    },
  },
  mounted() {
    if (this.getDataSources.length === 0) {
      this.isDataAvailable = false;
      this.fetchDataSources()
        .then(() => {
          this.isDataAvailable = true;
        })
        .catch(err => {
          this.isDataAvailable = true;
        });
    }
  },
};
</script>

<style lang="scss">
.new-entity {
  .header {
    border-bottom: 1px solid var(--border-color);
  }
  .custom-input {
    padding: 15px;
    border-radius: 5px;
    font-size: 1.2em;
    color: var(--font-color);

    &:focus {
      background: var(--border-color);
    }
  }

  .delete-icon {
    margin-top: 25px !important;
  }
  .entity-contents {
    display: flex;
    flex-direction: column;

    .schema-area {
      flex: 1;
      padding: 15px;
      border-right: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;

      @include respond-below(sm) {
        border-right: 0;
      }

      .schema-list {
        padding: 0;
        margin: 0;
        list-style-type: none;
        &:first-child {
          border-radius: 5px 5px 0 0;
        }

        &:last-child {
          border-radius: 0 0 5px 5px;
        }

        li {
          padding: 15px 15px;
          margin: 0;
          display: flex;
          justify-content: space-between;
          border-bottom: 1px solid var(--color-secondary);
          background-color: var(--border-color);

          p {
            padding: 0;
            margin: 0;

            &.caption {
              color: var(--color-primary);
            }
          }
        }
      }

      .dataset-selection {
        padding: 10px;
        border: 1px solid var(--border-color);
        border-radius: 5px;
        margin: 5px 10px;
      }

      .mw-200 {
        width: 200px;
        margin-right: 15px;
      }

      .mw-400 {
        width: 400px;
        margin-right: 15px;
      }
    }

    .details-area {
      min-width: 400px;
      padding: 15px;
      display: flex;
      flex-direction: column;
      @include respond-below(sm) {
        border-bottom: 1px solid var(--border-color);
      }

      p {
        margin: 0;
        padding: 5px 10px 10px 10px;
      }
    }
  }
}
</style>
